import { Observable } from 'rxjs';
import { APIResponseWrapper } from '../../models/common/api-response-wrapper';
import * as HrModels from '../hr.models';
import { Constructor, HrApiClient } from '../shared/hr-api-client';

export function withHrPerformanceApi<TBase extends Constructor<HrApiClient>>(Base: TBase) {
  return class extends Base {
    performanceDashboard(owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrPerformanceDashboard>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPerformanceDashboard>>(`${this.baseUrl}/performance/dashboard`, {
          params: this.params({ owningCompanyId })
        });
      }
    
    performanceCycles(owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrPerformanceCycle[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPerformanceCycle[]>>(`${this.baseUrl}/performance/cycles`, {
          params: this.params({ owningCompanyId })
        });
      }
    
    savePerformanceCycle(request: HrModels.HrPerformanceCycle): Observable<APIResponseWrapper<HrModels.HrPerformanceCycle>> {
        return request.performanceCycleId
          ? this.http.put<APIResponseWrapper<HrModels.HrPerformanceCycle>>(`${this.baseUrl}/performance/cycles/${request.performanceCycleId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrPerformanceCycle>>(`${this.baseUrl}/performance/cycles`, request);
      }
    
    activatePerformanceCycle(cycleId: number, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrPerformanceCycle>> {
        return this.http.post<APIResponseWrapper<HrModels.HrPerformanceCycle>>(`${this.baseUrl}/performance/cycles/${cycleId}/activate`, {}, {
          params: this.params({ owningCompanyId })
        });
      }
    
    performanceGoals(owningCompanyId?: number, employeeId?: number, cycleId?: number): Observable<APIResponseWrapper<HrModels.HrPerformanceGoal[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPerformanceGoal[]>>(`${this.baseUrl}/performance/goals`, {
          params: this.params({ owningCompanyId, employeeId, cycleId })
        });
      }
    
    savePerformanceGoal(request: HrModels.HrPerformanceGoal): Observable<APIResponseWrapper<HrModels.HrPerformanceGoal>> {
        return request.performanceGoalId
          ? this.http.put<APIResponseWrapper<HrModels.HrPerformanceGoal>>(`${this.baseUrl}/performance/goals/${request.performanceGoalId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrPerformanceGoal>>(`${this.baseUrl}/performance/goals`, request);
      }
    
    performanceReviews(owningCompanyId?: number, employeeId?: number, cycleId?: number): Observable<APIResponseWrapper<HrModels.HrPerformanceReview[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPerformanceReview[]>>(`${this.baseUrl}/performance/reviews`, {
          params: this.params({ owningCompanyId, employeeId, cycleId })
        });
      }
    
    savePerformanceReview(request: HrModels.HrPerformanceReview): Observable<APIResponseWrapper<HrModels.HrPerformanceReview>> {
        return request.performanceReviewId
          ? this.http.put<APIResponseWrapper<HrModels.HrPerformanceReview>>(`${this.baseUrl}/performance/reviews/${request.performanceReviewId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrPerformanceReview>>(`${this.baseUrl}/performance/reviews`, request);
      }
    
    submitPerformanceReview(reviewId: number, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrPerformanceReview>> {
        return this.http.post<APIResponseWrapper<HrModels.HrPerformanceReview>>(`${this.baseUrl}/performance/reviews/${reviewId}/submit`, {}, {
          params: this.params({ owningCompanyId })
        });
      }
    
    calibratePerformanceReview(reviewId: number, request: HrModels.HrPerformanceReview, owningCompanyId?: number): Observable<APIResponseWrapper<HrModels.HrPerformanceReview>> {
        return this.http.post<APIResponseWrapper<HrModels.HrPerformanceReview>>(`${this.baseUrl}/performance/reviews/${reviewId}/calibrate`, request, {
          params: this.params({ owningCompanyId })
        });
      }
    
    savePerformanceReviewSection(reviewId: number, request: HrModels.HrPerformanceReviewSection): Observable<APIResponseWrapper<HrModels.HrPerformanceReviewSection>> {
        return request.performanceReviewSectionId
          ? this.http.put<APIResponseWrapper<HrModels.HrPerformanceReviewSection>>(`${this.baseUrl}/performance/reviews/${reviewId}/sections/${request.performanceReviewSectionId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrPerformanceReviewSection>>(`${this.baseUrl}/performance/reviews/${reviewId}/sections`, request);
      }
    
    savePerformanceReviewRating(reviewId: number, request: HrModels.HrPerformanceReviewRating): Observable<APIResponseWrapper<HrModels.HrPerformanceReviewRating>> {
        return request.performanceReviewRatingId
          ? this.http.put<APIResponseWrapper<HrModels.HrPerformanceReviewRating>>(`${this.baseUrl}/performance/reviews/${reviewId}/ratings/${request.performanceReviewRatingId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrPerformanceReviewRating>>(`${this.baseUrl}/performance/reviews/${reviewId}/ratings`, request);
      }
    
    developmentPlans(owningCompanyId?: number, employeeId?: number): Observable<APIResponseWrapper<HrModels.HrDevelopmentPlan[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrDevelopmentPlan[]>>(`${this.baseUrl}/performance/development-plans`, {
          params: this.params({ owningCompanyId, employeeId })
        });
      }
    
    saveDevelopmentPlan(request: HrModels.HrDevelopmentPlan): Observable<APIResponseWrapper<HrModels.HrDevelopmentPlan>> {
        return request.developmentPlanId
          ? this.http.put<APIResponseWrapper<HrModels.HrDevelopmentPlan>>(`${this.baseUrl}/performance/development-plans/${request.developmentPlanId}`, request)
          : this.http.post<APIResponseWrapper<HrModels.HrDevelopmentPlan>>(`${this.baseUrl}/performance/development-plans`, request);
      }
    
    performanceKpiContext(owningCompanyId?: number, employeeId?: number): Observable<APIResponseWrapper<HrModels.HrPerformanceKpiContext[]>> {
        return this.http.get<APIResponseWrapper<HrModels.HrPerformanceKpiContext[]>>(`${this.baseUrl}/performance/kpi-context`, {
          params: this.params({ owningCompanyId, employeeId })
        });
      }
  };
}

